import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined'
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined'
import {
  Box,
  Container,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  SxProps,
  Theme,
  Typography,
} from '@mui/material'

import { File } from 'common/interfaces'
import { FileRemove } from 'elements/FileUpload/common/commonInterfaces'
import { filePreviewBox } from 'elements/FileUpload/common/styles'
import readableBytes from 'elements/FileUpload/common/util'

const styles: Record<string, SxProps<Theme>> = {
  left: {
    display: 'flex',
    alignItems: 'center',
    minWidth: 0,
    flex: 1,
  },
  icon: {
    mr: '18.5px',
    width: 24,
    height: 24,
    color: 'rgba(0, 0, 0, 0.54)',
  },
  info: {
    display: 'flex',
    flexDirection: 'column',
    gap: 0.5,
    color: 'text.primary',
  },
  name: {
    fontSize: 12,
    fontWeight: 500,
    lineHeight: 1.2,
    maxWidth: { xs: 180, sm: 320 },
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  meta: { fontSize: 11, color: 'text.secondary', lineHeight: 1.2 },
  selectWrap: {
    width: { xs: '100%', sm: 180 },
    mx: { xs: 0, sm: 2 },
    mt: { xs: 3, sm: 0 },
    flexShrink: 0,
    order: { xs: 2, sm: 'initial' },
    flexBasis: { xs: '100%', sm: 'auto' },
  },
  removeBtn: {
    color: 'text.secondary',
    order: { xs: 1, sm: 'initial' },
    ml: { xs: 'auto', sm: 0 },
    alignSelf: { xs: 'flex-start', sm: 'center' },
  },
}

interface FilePreviewProps extends FileRemove {
  file: File
  tag?: string
  tagLabel?: string
  tagOptions?: Array<{ value: string; label: string }>
  onTagChange?: (value: string) => void
}

export default function FilePreview({
  file,
  onRemove,
  tag = '',
  tagLabel = 'Document Type (Optional)',
  tagOptions,
  onTagChange,
}: FilePreviewProps): JSX.Element {
  const { name, extension, size } = file
  const { left, icon, info, name: nameSx, meta, selectWrap, removeBtn } = styles
  const hasOptions = Array.isArray(tagOptions) && tagOptions.length > 0

  return (
    <Box sx={{ ...filePreviewBox, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
      <Box sx={left}>
        <InsertDriveFileOutlinedIcon sx={icon} />
        <Container sx={info} disableGutters>
          <Box sx={nameSx}>{name}</Box>
          <Box sx={meta}>
            <span>{extension.toUpperCase()}</span> • <span>{readableBytes(size)}</span>
          </Box>
        </Container>
      </Box>

      {hasOptions && (
        <Box sx={selectWrap}>
          <FormControl fullWidth size="small">
            <InputLabel shrink>{tagLabel}</InputLabel>
            <Select
              value={tag}
              label={tagLabel}
              onChange={(e) => onTagChange?.(e.target.value)}
              displayEmpty
              renderValue={(val) =>
                val ? tagOptions.find((o) => o.value === val)?.label ?? String(val) : 'Select type…'
              }
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {tagOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}

      <IconButton sx={removeBtn} onClick={onRemove} aria-label="remove file">
        <CancelOutlinedIcon />
      </IconButton>
    </Box>
  )
}
